import { ApiError } from '@/app/lib/apiError'
import type { components } from '@/generated/api'

/** Schema types come from the generated file (never hand-written). */
export type Schemas = components['schemas']

/**
 * Base URL for the backend. Every route lives under `/api` on the same origin
 * (Vite proxies it in dev; the backend serves the app itself in production),
 * so there is no CORS configuration.
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api'

/** Where the browser keeps the access token. */
export const TOKEN_STORAGE_KEY = 'qmc.access-token'

/** Fired on `window` whenever the server answers 401, so the app can show the unlock screen. */
export const UNLOCK_EVENT = 'qmc:unlock-required'

export function getToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_STORAGE_KEY)
  } catch {
    return null
  }
}

export function setToken(token: string): void {
  try {
    window.localStorage.setItem(TOKEN_STORAGE_KEY, token)
  } catch {
    // Storage blocked: the token then lasts only until the next reload.
  }
}

export function clearToken(): void {
  try {
    window.localStorage.removeItem(TOKEN_STORAGE_KEY)
  } catch {
    // Nothing stored, nothing to clear.
  }
}

/** One entry of the `fields` list on a validation error. */
export type FieldIssue = Schemas['FieldIssue']

/**
 * The one client error model is echo's `ApiError` (copied verbatim, `@/app/lib/apiError`), so the
 * copied Quant code and QMC's own screens branch on the same class. `code` is the server's code,
 * or `network_error` / `http_<status>` / `timeout` for failures with no envelope.
 */
export { ApiError }

async function toApiError(response: Response): Promise<ApiError> {
  let body: unknown = null
  try {
    body = await response.json()
  } catch {
    body = null
  }
  // Any body with a `detail` (the envelope, a FastAPI 422 list, a dict) is read by echo's parser,
  // so a validation 422 shows its field text and a code-less body keeps `code: null`.
  if (isRecord(body) && body.detail !== undefined && body.detail !== null) {
    return new ApiError(response.status, body)
  }
  // No usable body (a proxy page, an old server): keep a stable code.
  return new ApiError(response.status, body, {
    code: `http_${response.status}`,
    message: `The server answered ${response.status}${response.statusText ? ` ${response.statusText}` : ''}.`,
  })
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

interface FetchOptions extends Omit<RequestInit, 'body'> {
  /** JSON-serialisable request body. */
  json?: unknown
  /** Abort the request after this many milliseconds (the error is `ApiError(0, ..., 'timeout')`). */
  timeoutMs?: number
  /** Caller abort signal; an abort rejects with the browser's `AbortError`. */
  signal?: AbortSignal | null
  /** Use this token instead of the stored one, and never raise the unlock event (the unlock screen's own check). */
  token?: string
}

/** Default request timeout, as echo's `apiFetch`: a hung request must reject, never leave a screen loading. */
export const API_TIMEOUT_MS = 15_000

/**
 * Surface a genuine backend failure to the signal terminal through the `echo:log` window event
 * (the seam `signalLog` listens on), as echo's `apiFetch` does: 5xx, unreachable and timeout only.
 */
function reportFailure(msg: string): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent('echo:log', { detail: { tag: 'err', msg } }))
}

/**
 * Typed fetch wrapper. Adds the bearer header when a token is stored, throws
 * `ApiError` on any failure, and raises the unlock event on a 401.
 */
export async function apiFetch<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const { json, token, headers, timeoutMs = API_TIMEOUT_MS, signal, ...init } = options
  const method = (init.method ?? 'GET').toUpperCase()
  const bearer = token ?? getToken()
  const requestHeaders: Record<string, string> = { Accept: 'application/json' }
  if (json !== undefined) requestHeaders['Content-Type'] = 'application/json'
  if (bearer) requestHeaders.Authorization = `Bearer ${bearer}`

  const controller = new AbortController()
  let timedOut = false
  const timer =
    timeoutMs === undefined
      ? null
      : setTimeout(() => {
          timedOut = true
          controller.abort()
        }, timeoutMs)
  const onAbort = (): void => controller.abort()
  if (signal) {
    if (signal.aborted) controller.abort()
    else signal.addEventListener('abort', onAbort, { once: true })
  }

  let response: Response
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      signal: controller.signal,
      headers: { ...requestHeaders, ...(headers as Record<string, string> | undefined) },
      body: json === undefined ? undefined : JSON.stringify(json),
    })
  } catch (error) {
    if (signal?.aborted && !timedOut) throw error instanceof DOMException ? error : new DOMException('Aborted', 'AbortError')
    if (timedOut) {
      reportFailure(`Backend unresponsive — ${method} ${path} timed out after ${timeoutMs}ms.`)
      throw new ApiError(0, { retryable: true }, { code: 'timeout', message: 'The request timed out.' })
    }
    reportFailure(`Backend unreachable — ${method} ${path}.`)
    throw new ApiError(0, { retryable: true }, { code: 'network_error', message: 'The server could not be reached.' })
  } finally {
    if (timer !== null) clearTimeout(timer)
    signal?.removeEventListener('abort', onAbort)
  }

  if (!response.ok) {
    const error = await toApiError(response)
    if (response.status >= 500) reportFailure(`${method} ${path} failed — ${response.status}.`)
    if (response.status === 401 && token === undefined) {
      window.dispatchEvent(new Event(UNLOCK_EVENT))
    }
    throw error
  }

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

/** GET /api/status: the one cheap call behind the status strip. */
export function getStatus(signal?: AbortSignal): Promise<Schemas['Status']> {
  return apiFetch<Schemas['Status']>('/status', { signal })
}

/**
 * Check a token the visitor typed. Succeeds when the server accepts it (or needs
 * none); rejects with `ApiError` code `unauthorized` when it does not.
 */
export async function checkToken(token: string): Promise<void> {
  await apiFetch<Schemas['Status']>('/status', { token })
}

/** The sandbox view WebSocket (same origin, `/api/sandbox/view`). */
export function sandboxViewUrl(): string {
  const base = new URL(API_BASE_URL, window.location.href)
  base.protocol = base.protocol === 'https:' ? 'wss:' : 'ws:'
  base.pathname = `${base.pathname.replace(/\/$/, '')}/sandbox/view`
  return base.toString()
}

/** Open the sandbox view socket; the token (when there is one) rides in the `bearer.<token>` subprotocol. */
export function openSandboxSocket(): WebSocket {
  const token = getToken()
  return token ? new WebSocket(sandboxViewUrl(), [`bearer.${token}`]) : new WebSocket(sandboxViewUrl())
}

// ---- Backtest runs ---------------------------------------------------------

export type Run = Schemas['Run']
export type RunDetail = Schemas['RunDetail']
export type RunStatus = Run['status']

export interface RunFilters {
  strategy?: string
  status?: RunStatus
}

/** GET /api/runs, newest first, optionally filtered by strategy and status. */
export function listRuns(filters: RunFilters = {}, signal?: AbortSignal): Promise<Schemas['RunsEnvelope']> {
  const query = new URLSearchParams()
  if (filters.strategy) query.set('strategy', filters.strategy)
  if (filters.status) query.set('status', filters.status)
  const suffix = query.size > 0 ? `?${query.toString()}` : ''
  return apiFetch<Schemas['RunsEnvelope']>(`/runs${suffix}`, { signal })
}

export function getRun(id: string, signal?: AbortSignal): Promise<RunDetail> {
  return apiFetch<RunDetail>(`/runs/${encodeURIComponent(id)}`, { signal })
}

/** POST /api/runs. Rejects with `cap_full` (409) when every slot is taken. */
export function startRun(strategy: string, params: Record<string, unknown>): Promise<RunDetail> {
  return apiFetch<RunDetail>('/runs', { method: 'POST', json: { strategy, params } satisfies Schemas['StartRunBody'] })
}

/** POST /api/runs/{id}/cancel: answers at once with the run still running; the poller finalizes it later. */
export function cancelRun(id: string): Promise<RunDetail> {
  return apiFetch<RunDetail>(`/runs/${encodeURIComponent(id)}/cancel`, { method: 'POST' })
}

/** DELETE /api/runs/{id}. Rejects with `run_running` (409) while the run is alive. */
export function deleteRun(id: string): Promise<void> {
  return apiFetch<void>(`/runs/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

/** One slice of a run's log, from a byte offset. */
export function getRunLog(id: string, offset: number, signal?: AbortSignal): Promise<Schemas['LogChunk']> {
  return apiFetch<Schemas['LogChunk']>(`/runs/${encodeURIComponent(id)}/log?offset=${offset}`, { signal })
}

/** The run's chart messages, trimmed to the last `bars` bars. */
export function getRunChart(id: string, bars: number, signal?: AbortSignal): Promise<Array<Record<string, unknown>>> {
  return apiFetch<Array<Record<string, unknown>>>(`/runs/${encodeURIComponent(id)}/chart.json?bars=${bars}`, { signal })
}

// ---- Live paper sessions ---------------------------------------------------

export type LiveSession = Schemas['LiveSession']
export type LiveStatus = LiveSession['status']
export type LiveAccount = Schemas['LiveAccount']

/** GET /api/live: sessions newest first with the active/max counts. */
export function listLive(status?: LiveStatus, signal?: AbortSignal): Promise<Schemas['LiveEnvelope']> {
  const suffix = status ? `?status=${encodeURIComponent(status)}` : ''
  return apiFetch<Schemas['LiveEnvelope']>(`/live${suffix}`, { signal })
}

export function getLive(id: string, signal?: AbortSignal): Promise<LiveSession> {
  return apiFetch<LiveSession>(`/live/${encodeURIComponent(id)}`, { signal })
}

/** POST /api/live. Answers after the server's settle window; rejects with a `live_*` code on a refused start. */
export function startLive(strategy: string, params: Record<string, unknown>, account: LiveAccount): Promise<LiveSession> {
  return apiFetch<LiveSession>('/live', { method: 'POST', json: { strategy, params, account } satisfies Schemas['StartLiveBody'] })
}

/** POST /api/live/{id}/stop: answers at once with the session `stopping`; the poller settles it later. */
export function stopLive(id: string): Promise<LiveSession> {
  return apiFetch<LiveSession>(`/live/${encodeURIComponent(id)}/stop`, { method: 'POST' })
}

/** DELETE /api/live/{id}. Rejects with `live_active` (409) while the session is alive. */
export function deleteLive(id: string): Promise<void> {
  return apiFetch<void>(`/live/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

/** One page of events after the byte cursor `after` (pass the previous page's `next`). */
export function getLiveEvents(id: string, after: number, signal?: AbortSignal): Promise<Schemas['LiveEvents']> {
  return apiFetch<Schemas['LiveEvents']>(`/live/${encodeURIComponent(id)}/events?after=${after}`, { signal })
}

export function getLiveEquity(id: string, maxPoints: number, signal?: AbortSignal): Promise<Schemas['LiveEquity']> {
  return apiFetch<Schemas['LiveEquity']>(`/live/${encodeURIComponent(id)}/equity?max_points=${maxPoints}`, { signal })
}

export function getLiveLog(id: string, offset: number, signal?: AbortSignal): Promise<Schemas['LogChunk']> {
  return apiFetch<Schemas['LogChunk']>(`/live/${encodeURIComponent(id)}/log?offset=${offset}`, { signal })
}

// ---- Strategy preview ------------------------------------------------------

export type PreviewBody = Pick<Schemas['PreviewRequest'], 'instrument' | 'tf' | 'bars' | 'params'>
export type PreviewResult = Schemas['PreviewResponse']

/**
 * POST /api/strategies/{slug}/preview: one one-shot compute (5 to 25 s). Aborting the signal
 * cancels the upstream call, which is how a newer request supersedes an older one.
 */
export function previewStrategy(slug: string, body: PreviewBody, signal?: AbortSignal): Promise<PreviewResult> {
  return apiFetch<PreviewResult>(`/strategies/${encodeURIComponent(slug)}/preview`, {
    method: 'POST',
    json: { ...body, include_candles: true } satisfies Schemas['PreviewRequest'],
    signal,
  })
}

// ---- Data coverage, instrument sources, saved configs ----------------------

export type InstrumentCoverage = Schemas['InstrumentCoverage']
export type InstrumentSources = Schemas['InstrumentSources']
export type SavedConfig = Schemas['SavedConfigOut']

/** GET /api/data: per-instrument stored-data coverage. */
export function getDataCoverage(signal?: AbortSignal): Promise<Schemas['DataCoverage']> {
  return apiFetch<Schemas['DataCoverage']>('/data', { signal })
}

/** GET /api/instrument-sources: where each instrument's data comes from. */
export function getInstrumentSources(signal?: AbortSignal): Promise<InstrumentSources> {
  return apiFetch<InstrumentSources>('/instrument-sources', { signal })
}

export function listSavedConfigs(slug: string, signal?: AbortSignal): Promise<Schemas['SavedConfigsEnvelope']> {
  return apiFetch<Schemas['SavedConfigsEnvelope']>(`/strategies/${encodeURIComponent(slug)}/configs`, { signal })
}

/** PUT creates or overwrites; rejects with `config_limit` (409), `invalid_config` or `invalid_config_name` (422). */
export function saveConfig(slug: string, name: string, params: Record<string, unknown>): Promise<SavedConfig> {
  return apiFetch<SavedConfig>(`/strategies/${encodeURIComponent(slug)}/configs/${encodeURIComponent(name)}`, {
    method: 'PUT',
    json: { params } satisfies Schemas['SavedConfigBody'],
  })
}

/** DELETE is idempotent (204 whether or not the config existed). */
export function deleteConfig(slug: string, name: string): Promise<void> {
  return apiFetch<void>(`/strategies/${encodeURIComponent(slug)}/configs/${encodeURIComponent(name)}`, { method: 'DELETE' })
}
